import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiBriefcase, FiAlertCircle, FiCheck, FiInfo, FiX } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { addNotification } from '../../../lib/notifications';
import { getAccountOwnerId } from '../utils/ownerEntities';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import { getBusinessTypes, setBusinessType, BusiCategory, BusiType } from '../coaTemplateApi';
import BusinessPickerTiles, { CoaSetupEntity, getCoaSetupEntities } from './BusinessPickerTiles';
import './GLChartOfAccountsTab.css';

// =============================================
// Type of Business tab — pick a business's INDUSTRY type (not its legal form).
// The category is auto-derived from the chosen type. SEVERAL types can be chosen (an
// accounting practice that also owns rentals): all go to industry_type_ids, the first —
// the main activity — to industry_type_id (user_businesses).
// People are listed too: an individual can have an activity of their own to keep books
// for (rental income, a sole proprietorship) - theirs is written to users.industry_type_id.
// =============================================
interface Props {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

function resolveAccountOwnerId(users: Record<string, any>[]): number {
  return getAccountOwnerId(users) ?? 0;
}

function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const s = JSON.parse(raw);
    const id = Number(s.id || s.user_id);
    return id || null;
  } catch { return null; }
}

// Category for a person with no business (migration 129) — not offered to a business.
const NO_BUSINESS_CATEGORY = 'no_business';

const TypeOfBusinessTab: React.FC<Props> = ({ viewMode = 'owner', selectedClientId = null }) => {
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [businesses, setBusinesses] = useState<Record<string, any>[]>([]);
  const [categories, setCategories] = useState<BusiCategory[]>([]);
  const [types, setTypes] = useState<BusiType[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // every type chosen, main activity first
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [savedMsg, setSavedMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Load reference data + entities ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRows, bizRows] = await Promise.all([
          loadTable('users').catch(() => getTableData('users')),
          loadTable('user_businesses').catch(() => getTableData('user_businesses')),
        ]);
        const ref = await getBusinessTypes();
        if (cancelled) return;
        setUsers(usersRows);
        setBusinesses(bizRows);
        setCategories(ref.categories);
        setTypes(ref.types);
        setLoaded(true);
      } catch (e: any) {
        if (cancelled) return;
        setLoadError(e?.message || 'Could not load business types.');
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accountOwnerId = useMemo(() => resolveAccountOwnerId(users), [users]);

  // ---- People + businesses for this mode ----
  const bizEntities = useMemo<CoaSetupEntity[]>(
    () => getCoaSetupEntities(viewMode, accountOwnerId, selectedClientId, users, businesses),
    [users, businesses, accountOwnerId, viewMode, selectedClientId]);

  const selectedBiz = useMemo(
    () => bizEntities.find(b => b.key === selectedKey) || null,
    [bizEntities, selectedKey]);

  // Auto-select the only entry, and seed the type from its stored value.
  useEffect(() => {
    if (bizEntities.length === 1 && selectedKey == null) {
      setSelectedKey(bizEntities[0].key);
    }
  }, [bizEntities, selectedKey]);

  useEffect(() => {
    if (selectedBiz) setSelectedTypeIds(selectedBiz.industry_type_ids.map(String));
  }, [selectedBiz]);

  const toggleType = useCallback((v: string) => {
    setSelectedTypeIds(prev => (prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]));
  }, []);

  // ---- Type dropdown options grouped by category ----
  // A business is never "No business": that category is offered to people only (a
  // type from it already saved on a business stays listed so it can be removed).
  const selectedIsPerson = selectedBiz ? selectedBiz.is_person : true;
  const typeOptions = useMemo<DropdownOption[]>(() => {
    const catById = new Map(categories.map(c => [c.busi_category_id, c]));
    return types
      .filter(t => selectedIsPerson
        || catById.get(t.busi_category_id)?.category_code !== NO_BUSINESS_CATEGORY
        || selectedTypeIds.includes(String(t.busi_type_id)))
      .map(t => {
        const cat = catById.get(t.busi_category_id);
        return {
          value: String(t.busi_type_id),
          label: t.type_name,
          group: cat?.category_name || 'Other',
          // found by a related word too: the type's own other words + its category
          keywords: [t.search_keywords, cat?.category_name].filter(Boolean).join(', '),
        };
      });
  }, [types, categories, selectedIsPerson, selectedTypeIds]);

  const selectedTypes = useMemo(
    () => selectedTypeIds.map(id => types.find(t => String(t.busi_type_id) === id)).filter(Boolean) as BusiType[],
    [types, selectedTypeIds]);

  // Categories of the chosen types, each once, in the order chosen.
  const derivedCategories = useMemo(() => {
    const names: string[] = [];
    selectedTypes.forEach(t => {
      const c = categories.find(x => x.busi_category_id === t.busi_category_id);
      if (c && !names.includes(c.category_name)) names.push(c.category_name);
    });
    return names;
  }, [selectedTypes, categories]);

  const storedIds = selectedBiz ? selectedBiz.industry_type_ids.map(String) : [];
  const isDirty = !!selectedBiz && selectedTypeIds.join(',') !== storedIds.join(',');
  const isChange = isDirty && storedIds.length > 0;

  useEffect(() => { setFormDirty(isDirty); }, [isDirty]);
  // A flag left set when this tab goes away would follow the user to the next screen.
  useEffect(() => () => setFormDirty(false), []);

  // ---- Save ----
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!selectedBiz) return false;
    const ids = selectedTypeIds.map(Number);
    const typeNames = selectedTypes.map(t => t.type_name).join(', ');
    setSaving(true);
    try {
      await setBusinessType({
        owner_type: selectedBiz.owner_type,
        owner_id: selectedBiz.owner_id,
        busi_type_ids: ids,
      });
      // Reflect locally so the tab + Setup GL Accounts see the new value immediately.
      const patchRow = { industry_type_id: ids[0] ?? null, industry_type_ids: ids };
      if (selectedBiz.is_person) {
        setUsers(prev => prev.map((u: any) =>
          Number(u.user_id) === selectedBiz.owner_id ? { ...u, ...patchRow } : u));
      } else {
        setBusinesses(prev => prev.map((b: any) =>
          Number(b.business_id) === selectedBiz.owner_id ? { ...b, ...patchRow } : b));
      }
      setFormDirty(false);
      setSavedMsg(typeNames ? `Saved: ${typeNames}` : 'Type cleared.');
      setTimeout(() => setSavedMsg(''), 3500);

      // Notify the account owner if a sub-user made the change.
      const uid = currentUserId();
      if (uid && uid !== accountOwnerId && accountOwnerId) {
        const actor = users.find((u: any) => Number(u.user_id) === uid);
        const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : 'A user';
        addNotification({
          account_owner_id: accountOwnerId,
          type: 'busi_type_set',
          title: 'Business type set',
          message: `${actorName} set "${selectedBiz.display_name}" as ${typeNames || 'no type'}.`,
          actor_user_id: uid,
          actor_name: actorName,
        });
      }
      return true;
    } catch (e: any) {
      setSavedMsg('');
      setLoadError(e?.message || 'Failed to save business type.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [selectedBiz, selectedTypeIds, selectedTypes, accountOwnerId, users]);

  useEffect(() => {
    registerSaveHandler(() => handleSave());
    return () => registerSaveHandler(null);
  }, [handleSave]);

  // ---- Render ----
  if (!loaded) {
    return <div className="coa-tab"><div className="coa-empty"><p>Loading business types…</p></div></div>;
  }

  if (viewMode === 'clients' && !selectedClientId) {
    return <div className="coa-tab"><div className="coa-empty"><p>Select a client first.</p></div></div>;
  }

  return (
    <div className="coa-tab">
      <div className="coa-info-banner">
        Choose the <strong>type of business</strong> for each business. We use it to suggest a starting
        Chart of Accounts. The industry <strong>category is filled in automatically</strong> from your choice.
        Choose <strong>more than one</strong> where there are several activities, e.g. an accounting practice
        that also owns long-term and short-term rentals.
        A person with an activity of their own (rental income, for example) can be given a type too,
        so its accounts are ready for their tax return.
      </div>

      {loadError && (
        <div className="coa-field-error" style={{ marginBottom: 12 }}>
          <FiAlertCircle size={14} /> {loadError}
        </div>
      )}

      {bizEntities.length === 0 ? (
        <div className="coa-empty">
          <div className="coa-empty-icon"><FiBriefcase size={40} /></div>
          <p>Nobody found for this {viewMode === 'clients' ? 'client' : 'account'}.
            Add a person or a business in <strong>Family &amp; Businesses</strong> first.</p>
        </div>
      ) : (
        <>
          {/* Who the type is for (only when more than one) */}
          {bizEntities.length > 1 && (
            <BusinessPickerTiles
              entities={bizEntities}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              typeName={(id) => types.find(t => t.busi_type_id === id)?.type_name}
            />
          )}

          {selectedBiz && (
            <div className="coa-form-card">
              <div className="coa-field-group">
                <label className="coa-field-label">
                  {selectedBiz.is_person ? `Type of activity for ${selectedBiz.display_name}` : 'Type of Business'} <span className="coa-required">*</span>
                </label>
                <SearchableDropdown
                  multiSelect
                  options={typeOptions}
                  selectedValues={selectedTypeIds}
                  onToggleValue={toggleType}
                  placeholder={selectedBiz.is_person ? '-- Choose one or more activities, e.g. Landlord --' : '-- Choose one or more business types --'}
                />
                {selectedTypes.length > 0 && (
                  <div className="coa-type-chips">
                    {selectedTypes.map((t, i) => (
                      <span key={t.busi_type_id} className="coa-type-chip">
                        {t.type_name}
                        {i === 0 && selectedTypes.length > 1 && <em>main</em>}
                        <button type="button" title={`Remove ${t.type_name}`} aria-label={`Remove ${t.type_name}`}
                          onClick={() => toggleType(String(t.busi_type_id))}>
                          <FiX size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {isChange && (
                  <span className="coa-field-hint" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, color: 'var(--amber-600, #b45309)' }}>
                    <FiInfo size={13} />
                    Changing the types won't touch accounts you've already created — Setup GL Accounts just offers the accounts of the types chosen now.
                  </span>
                )}
              </div>

              {/* Auto-derived category (read-only) */}
              <div className="coa-field-group">
                <label className="coa-field-label">{derivedCategories.length > 1 ? 'Industry Categories' : 'Industry Category'} (auto-detected)</label>
                <input
                  className="coa-search-input"
                  type="text"
                  readOnly
                  value={derivedCategories.join(' · ')}
                  placeholder={selectedBiz.is_person ? 'Pick an activity above' : 'Pick a business type above'}
                  style={{ background: '#f5f5f5', color: '#555' }}
                />
              </div>

              <div className="coa-action-bar" style={{ marginTop: 8 }}>
                <button
                  className="coa-btn-primary"
                  disabled={!isDirty || saving}
                  onClick={handleSave}
                >
                  <FiCheck size={15} /> {saving ? 'Saving…' : (selectedBiz.is_person
                    ? (selectedBiz.industry_type_id ? 'Update activity types' : 'Save activity types')
                    : (selectedBiz.industry_type_id ? 'Update business types' : 'Save business types'))}
                </button>
                {savedMsg && <span className="coa-saved-msg" style={{ marginLeft: 12 }}>{savedMsg}</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TypeOfBusinessTab;
